import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    Tooltip,
    IconButton,
} from '@mui/material';

// Component
import AttachmentViewer from './AttachmentViewer';
import { Toast } from '../../../components';
import { processAttachment } from './AttachmentViewer';
import AutoCompleteFormControl from '../controlled/AutoCompleteFormControl';

// Icons
import {
    PaperPlaneRight,
    Paperclip,
    XCircle,
    Eye,
} from '@phosphor-icons/react';

// API hooks and types
import {
    useQueryAlerts,
    useQueryGetAlertsByOrganizationId,
    useMutationSendMessageAlert,
} from 'src/modules/hooks/apis';
import { postAlertsPageSendMessage } from 'src/modules/generated-api-clients/sql';
// For email view reply
import {
    useMutationPutMessageThread,
    useQueryGetSingleAlertThread,
    useQueryGetAlertThreadUserList,
    useMutationPutAlertMsgThread,
} from 'src/modules/hooks/apis/sql/threads';

// Convert file to base64 (defined in this component)
export const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = error => reject(error);
    });
};

export const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setAttachedFiles: React.Dispatch<React.SetStateAction<File[]>>,
    setToastMessage: React.Dispatch<React.SetStateAction<string>>,
    setToastOpen: React.Dispatch<React.SetStateAction<boolean>>,
    fileInputRef: React.RefObject<HTMLInputElement>
) => {
    if (event.target.files) {
        const files = Array.from(event.target.files);
        const allowedExtensions = [
            '.pdf',
            '.jpg',
            '.jpeg',
            '.png',
            '.gif',
            '.xlsx',
            '.xls',
            '.csv',
        ];
        const maxFileSize = 20 * 1024 * 1024;
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        files.forEach(file => {
            const fileExtension = file.name
                .substring(file.name.lastIndexOf('.'))
                .toLowerCase();
            if (
                allowedExtensions.includes(fileExtension) &&
                file.size <= maxFileSize
            ) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name);
            }
        });

        setAttachedFiles(prevFiles => [...prevFiles, ...validFiles]);

        if (invalidFiles.length > 0) {
            const invalidFileNames = invalidFiles.join(', ');
            const reason = files.some(file => file.size > maxFileSize)
                ? 'Exceeded the maximum size of 20MB'
                : 'Unsupported file type';
            setToastMessage(`Error: ${reason}: ${invalidFileNames}`);
            setToastOpen(true);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }
};

export const handleDeleteFile = (
    index: number,
    setAttachedFiles: React.Dispatch<React.SetStateAction<File[]>>
) => {
    setAttachedFiles(prevFiles => {
        const updatedFiles = [...prevFiles];
        updatedFiles.splice(index, 1);
        return updatedFiles;
    });
};

interface Message {
    user_id: string;
    message_id?: string;
    time_stamp: string;
    user_email: string;
    message_text: string;
    user_last_name: string;
    user_first_name: string;
    attached_files: Array<string>;
}

interface CommunicationMessageProps {
    alertMessages?: Message[];
    userEmail: string;
    alert_id?: string;
    view?: string;
    client_id?: string;
    new_email?: boolean;
    basePath?: string;
    thread_id?: string;
    read_list?: { full_name: string; read_time: string }[];
    onInvoiceUrlReceived?: (url: string) => void;
}

const CommunicationMessage: React.FC<CommunicationMessageProps> = ({
    alertMessages,
    userEmail,
    alert_id,
    view,
    client_id,
    new_email,
    basePath,
    thread_id,
    read_list,
    onInvoiceUrlReceived,
}) => {
    const [replyText, setReplyText] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const [remainingCharacters, setRemainingCharacters] = useState(200);
    const maxCharacterLimit = 200;

    // For new email view
    const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
    const [selectedRecipientsIds, setSelectedRecipientsIds] = useState<
        string[]
    >([]);
    const [subject, setSubject] = useState<string>('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>(
        'success'
    );

    const isAlertsMessage = view === 'admin';
    const isClientAlertsMessage = view === 'client';
    const isEmailView = view === 'email';
    const isEmailAlertView = view === 'email-alert';

    const { data, refetch: refetchSingleAlert } = useQueryGetSingleAlertThread(
        thread_id || '',
        {
            enabled: !!thread_id && !!isEmailAlertView,
        }
    );

    const { data: userListData } = useQueryGetAlertThreadUserList(
        alert_id || '',
        {
            enabled: !!new_email,
        }
    );

    const { data: alertsData, refetch: refetchAlerts } = useQueryAlerts({
        enabled: !!isAlertsMessage,
    });

    const { data: clientAlertsData, refetch: refetchClientAlerts } =
        useQueryGetAlertsByOrganizationId(client_id ?? '', {
            enabled: !!isClientAlertsMessage,
        });

    // When data becomes available, extract invoice_url and pass it to the parent
    useEffect(() => {
        console.log('view', view);
        if (
            isEmailAlertView &&
            data?.body?.[0]?.invoice_url &&
            onInvoiceUrlReceived
        ) {
            onInvoiceUrlReceived(data.body[0].invoice_url);
        } else if (
            isAlertsMessage &&
            alertsData?.body &&
            onInvoiceUrlReceived
        ) {
            const matchingAlert = alertsData.body.find(
                alert => alert.id === alert_id
            );
            if (matchingAlert?.invoice_url) {
                onInvoiceUrlReceived(matchingAlert.invoice_url);
            }
        } else if (
            isClientAlertsMessage &&
            clientAlertsData?.body?.alerts &&
            onInvoiceUrlReceived
        ) {
            const matchingAlert = clientAlertsData.body.alerts.find(
                alert => alert.id === alert_id
            );
            if (matchingAlert?.invoice_url) {
                onInvoiceUrlReceived(matchingAlert.invoice_url);
            }
        }
    }, [data, isEmailAlertView, onInvoiceUrlReceived]);

    const messages = isAlertsMessage
        ? alertsData?.body.find(alert => alert.id === alert_id)
              ?.messages_json || []
        : isClientAlertsMessage
          ? clientAlertsData?.body.alerts.find(alert => alert.id === alert_id)
                ?.messages_json || []
          : isEmailAlertView
            ? data?.body[0].messages_json || []
            : isEmailView
              ? alertMessages || []
              : [];

    const recipients = userListData?.map((user: any) => user.user_email) ?? [];

    const refetchMessages = async () => {
        if (isAlertsMessage) {
            await refetchAlerts();
        } else if (isClientAlertsMessage) {
            await refetchClientAlerts();
        } else if (isEmailAlertView) {
            await refetchSingleAlert();
        }
    };

    const formatDate = (timestamp: any) => {
        const date = new Date(timestamp);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        }).format(date);
    };

    const handleRecipientChange = (
        event: React.ChangeEvent<{}>,
        value: string | string[] | null
    ) => {
        if (value) {
            const selectedEmails = Array.isArray(value) ? value : [value];
            setSelectedRecipients(selectedEmails);
            // Search for the matching user IDs from userListData based on the selected emails.
            const selectedIds = userListData
                ?.filter((user: any) =>
                    selectedEmails.includes(user.user_email)
                )
                .map((user: any) => user.id);
            setSelectedRecipientsIds(selectedIds || []);
        } else {
            setSelectedRecipients([]);
            setSelectedRecipientsIds([]);
        }
    };

    const handleReplyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setReplyText(newText);
        setRemainingCharacters(maxCharacterLimit - newText.length);
    };

    // Choose the appropriate mutation hook based on view
    const { mutate: sendEmailMessage } = useMutationPutMessageThread();
    const { mutate: sendAlertMessage } = useMutationSendMessageAlert();
    const { mutate: sendAlertThread } = useMutationPutAlertMsgThread();

    const handleReplySubmit = async () => {
        if (replyText.trim()) {
            try {
                const convertedFiles = await Promise.all(
                    attachedFiles.map(async file => ({
                        file_name: file.name,
                        file_data: await convertFileToBase64(file),
                    }))
                );

                if (isEmailView) {
                    // For email view, build the payload using thread_id from props
                    const requestBody = {
                        thread_id: thread_id ?? '',
                        email_text: replyText.trim(),
                        attachments: convertedFiles,
                    };
                    sendEmailMessage(requestBody, {
                        onSuccess: async () => {
                            console.log('Email message sent successfully!');
                            setToastSeverity('success');
                            setToastMessage('Email message sent successfully!');
                            setToastOpen(true);
                            setReplyText('');
                            setAttachedFiles([]);
                            setRemainingCharacters(maxCharacterLimit);
                            await refetchMessages();
                        },
                        onError: error => {
                            console.error(
                                'Error sending email message:',
                                error
                            );
                            setToastSeverity('error');
                            setToastMessage('Error sending email message.');
                            setToastOpen(true);
                        },
                    });
                } else if (new_email === true) {
                    // For new email thread, build the payload using sendAlertThread mutation
                    // Make sure selectedRecipientsId is populated from your userListData
                    const requestBody = {
                        alert_id: alert_id ?? '',
                        user_list: selectedRecipientsIds, // Ensure this state contains the corresponding user IDs
                        subject: subject, // Subject from state
                        email_text: replyText.trim(),
                        attachments: convertedFiles,
                    };
                    sendAlertThread(requestBody, {
                        onSuccess: async () => {
                            console.log(
                                'Alert thread message sent successfully!'
                            );
                            setToastSeverity('success');
                            setToastMessage(
                                'Alert thread message sent successfully!'
                            );
                            setToastOpen(true);
                            setReplyText('');
                            setAttachedFiles([]);
                            setSelectedRecipients([]);
                            setSelectedRecipientsIds([]);
                            setSubject('');
                            setRemainingCharacters(maxCharacterLimit);
                            await refetchMessages();
                        },
                        onError: error => {
                            console.error(
                                'Error sending alert thread message:',
                                error
                            );
                            setToastSeverity('error');
                            setToastMessage(
                                'Error sending alert thread message.'
                            );
                            setToastOpen(true);
                        },
                    });
                } else {
                    // For alerts view, use the existing payload structure
                    const requestBody = {
                        alert_id: alert_id ?? '',
                        message_text: replyText.trim(),
                        attached_files: convertedFiles,
                    };
                    sendAlertMessage(requestBody, {
                        onSuccess: async () => {
                            console.log('Alert message sent successfully!');
                            setToastSeverity('success');
                            setToastMessage('Alert message sent successfully!');
                            setToastOpen(true);
                            setReplyText('');
                            setAttachedFiles([]);
                            setRemainingCharacters(maxCharacterLimit);
                            await refetchMessages();
                        },
                        onError: error => {
                            console.error(
                                'Error sending alert message:',
                                error
                            );
                            setToastSeverity('error');
                            setToastMessage('Error sending alert message.');
                            setToastOpen(true);
                        },
                    });
                }
            } catch (error) {
                console.error('Error preparing message submission:', error);
                setToastSeverity('error');
                setToastMessage('Error processing attached files.');
                setToastOpen(true);
            }
        }
    };

    // ==================================== TOAST ====================================
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const handleToastClose = () => {
        setToastOpen(false);
    };

    // At the top of your render (inside the component function), do:
    const reversedMessages = messages ? [...messages].reverse() : [];

    let newestUserMessageIndex = -1;
    for (let i = reversedMessages.length - 1; i >= 0; i--) {
        if (reversedMessages[i].user_email === userEmail) {
            newestUserMessageIndex = i;
            break;
        }
    }

    // Create a ref for the bottom dummy element
    const bottomRef = useRef<HTMLDivElement>(null);

    // When reversedMessages change (after refetch), scroll the bottom element into view
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [reversedMessages]);

    return (
        <Box>
            <Toast
                id="fileUploadToast"
                isOpen={toastOpen}
                message={toastMessage}
                severity={toastSeverity}
                onClose={handleToastClose}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 500px)',
                    minHeight: '600px',
                    overflowY: 'auto',
                    p: 2,
                }}
            >
                {reversedMessages && reversedMessages.length > 0 ? (
                    reversedMessages.map((message: any, index: number) => {
                        const isCurrentUser = message.user_email === userEmail;
                        const isNewestUserMsg =
                            isCurrentUser &&
                            index === newestUserMessageIndex &&
                            read_list;
                        const names = Array.isArray(read_list)
                            ? read_list.map((r: any) => r.full_name)
                            : [];
                        const filteredNames = names.filter(
                            (name: string) =>
                                name.toLowerCase() !==
                                `${message.user_first_name} ${message.user_last_name}`.toLowerCase()
                        );
                        return (
                            <Box key={index} sx={{ py: 2 }}>
                                <Grid
                                    container
                                    spacing={2}
                                    justifyContent={
                                        isCurrentUser
                                            ? 'flex-end'
                                            : 'flex-start'
                                    }
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        md={8}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: isCurrentUser
                                                ? 'row-reverse'
                                                : 'row',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <Paper
                                            sx={{
                                                p: 2,
                                                borderRadius: '12px',
                                                maxWidth: '85%',
                                                minWidth: 'fit-content',
                                                bgcolor: isCurrentUser
                                                    ? '#D6E6F5'
                                                    : '#F1F1F1',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: isCurrentUser
                                                        ? '#003366'
                                                        : '#333',
                                                }}
                                            >
                                                {isCurrentUser
                                                    ? ''
                                                    : `${message.user_first_name} ${message.user_last_name}`}
                                            </Typography>
                                            <Typography
                                                id="messageReceivedContent"
                                                sx={{ mt: 1 }}
                                            >
                                                {message.message_text}
                                            </Typography>
                                            {(message.attached_files || [])
                                                .length > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 2,
                                                        mt: 2,
                                                    }}
                                                >
                                                    <AttachmentViewer
                                                        attachments={
                                                            message.attached_files
                                                        }
                                                        basePath={
                                                            basePath || ''
                                                        }
                                                    />
                                                </Box>
                                            )}
                                        </Paper>
                                        {isNewestUserMsg &&
                                            filteredNames.length > 0 && (
                                                <Tooltip
                                                    title={read_list
                                                        ?.map(
                                                            (r: any) =>
                                                                r.full_name
                                                        )
                                                        .filter(
                                                            (name: string) =>
                                                                name.toLowerCase() !==
                                                                `${message.user_first_name} ${message.user_last_name}`.toLowerCase()
                                                        )
                                                        .join(', ')}
                                                    placement="top"
                                                >
                                                    <Eye
                                                        size={18}
                                                        color="#1D4A74"
                                                        weight="duotone"
                                                    />
                                                </Tooltip>
                                            )}
                                    </Grid>
                                </Grid>
                                <Typography
                                    id="messageReceiverTimestamp"
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        textAlign: isCurrentUser
                                            ? 'right'
                                            : 'left',
                                        mt: 0.5,
                                        color: '#666',
                                    }}
                                >
                                    {formatDate(message.time_stamp)}
                                </Typography>
                            </Box>
                        );
                    })
                ) : (
                    <Typography
                        variant="subtitle1"
                        sx={{ mt: 2, textAlign: 'center' }}
                    >
                        No communications to display.
                    </Typography>
                )}
                <div ref={bottomRef} />
            </Box>
            <Box
                sx={{
                    position: 'sticky',
                    bottom: -20,
                    backgroundColor: 'white',
                    zIndex: 1000,
                    p: 2,
                }}
            >
                {new_email && (
                    <>
                        <AutoCompleteFormControl
                            label="To"
                            options={recipients || []}
                            value={selectedRecipients}
                            onChange={handleRecipientChange}
                            getOptionLabel={option => option}
                            id="organization"
                            name="organization"
                            size="medium"
                            multiple={true}
                            variant="standard"
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Subject"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            variant="standard"
                            size="small"
                            sx={{ mb: 1 }}
                        />
                    </>
                )}
                {attachedFiles.map((file, index) => {
                    const { shortFilename, fileIcon, iconColor } =
                        processAttachment(file.name, '', 35);
                    return (
                        <Paper
                            key={index}
                            sx={{
                                p: 1,
                                mb: 1,
                                bgcolor: '#F2F2F2',
                                border: '1.5px solid #4D4D4D',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box sx={{ mr: 1 }}>
                                        {(() => {
                                            const IconComponent = fileIcon;
                                            return (
                                                <IconComponent
                                                    size={24}
                                                    color={iconColor}
                                                    weight="fill"
                                                />
                                            );
                                        })()}
                                    </Box>
                                    <Typography
                                        sx={{
                                            wordWrap: 'break-word',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {shortFilename}
                                    </Typography>
                                </Box>
                                <Box textAlign="right">
                                    <IconButton
                                        onClick={() =>
                                            handleDeleteFile(
                                                index,
                                                setAttachedFiles
                                            )
                                        }
                                        color="inherit"
                                        size="small"
                                    >
                                        <XCircle size={24} color="#4D4D4D" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
                <TextField
                    label="Reply"
                    value={replyText}
                    onChange={handleReplyChange}
                    multiline
                    rows={4}
                    fullWidth
                    // onKeyDown={e => {
                    //     // If the user presses Enter (and not Shift), and replyText is non-empty,
                    //     // submit the message. Otherwise, let them insert a newline.
                    //     if (
                    //         e.key === 'Enter' &&
                    //         !e.shiftKey &&
                    //         replyText.trim()
                    //     ) {
                    //         e.preventDefault(); // Prevent adding a newline
                    //         handleReplySubmit(); // Trigger the send logic
                    //     }
                    // }}
                />
                <Typography id="remainingCharactersInfo" variant="caption">
                    You have {remainingCharacters} characters remaining
                </Typography>
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                    <Tooltip title="Attachment" sx={{ mr: 1 }}>
                        <IconButton
                            id="attachButton"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Paperclip size={24} color="#4D4D4D" />
                            <input
                                id="fileInput"
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={event =>
                                    handleFileInputChange(
                                        event,
                                        setAttachedFiles,
                                        setToastMessage,
                                        setToastOpen,
                                        fileInputRef
                                    )
                                }
                                multiple
                            />
                        </IconButton>
                    </Tooltip>
                    <Button
                        startIcon={
                            <PaperPlaneRight
                                size={20}
                                color="#ffffff"
                                weight="fill"
                            />
                        }
                        variant="contained"
                        color="primary"
                        onClick={handleReplySubmit}
                    >
                        Send
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CommunicationMessage;
